import { models, stores } from '@kurtosys/app-start';
import { components, shared } from '@kurtosys/ksys-app-components';
import { common } from '@kurtosys/ksys-app-template';
import { IManifest } from '@kurtosys/types/appsManager/index.js';
import { action, computed, makeObservable, observable } from 'mobx';

import { LIBRARY_COMPONENTS_CONFIGURATION } from '../../../configuration/libraryComponentsConfiguration.js';
import { MockData } from '../../../configuration/mockData';
import { IComponentStyles } from '../../../models/app/IComponentStyles.js';
import { IConfiguration } from '../../../models/app/IConfiguration.js';
import { TStoreContext } from '../../../models/app/TStoreContext.js';
import { Feature } from '../../shared/Feature.js';
import { IAppComponents } from '../models/IAppComponents.js';

/* [Component: appStoreComponentImport] */

type ISummaryBlocksProps = components.fact.SummaryBlocks.models.ISummaryBlocksProps;
type ISummaryBlockProps = components.fact.SummaryBlock.models.ISummaryBlockProps;
type ICardProps = components.base.Card.models.ICardProps;

const factHasValue = shared.factHasValue;

let devConfig: models.IAppDevelopmentConfig = {
	applicationClientConfigurationIds: null,
	applicationClientConfigurations: null,
	authentication: null,
	configuration: null,
	styles: null,
	libraryComponentsConfiguration: LIBRARY_COMPONENTS_CONFIGURATION,
};

if (process.env.NODE_ENV !== 'production') {
	devConfig = {
		...devConfig,
		applicationClientConfigurationIds: async () => {
			const { APPLICATION_CLIENT_CONFIGURATION_IDS } = await import(
				'../../../configuration/development.applicationClientConfigurationIds.js'
			);
			return APPLICATION_CLIENT_CONFIGURATION_IDS;
		},
		applicationClientConfigurations: async () => {
			const { APPLICATION_CLIENT_CONFIGURATIONS } = await import(
				'../../../configuration/development.applicationClientConfigurations.js'
			);
			return APPLICATION_CLIENT_CONFIGURATIONS;
		},
		authentication: async () => {
			const { AUTHENTICATION } = await import('../../../configuration/development.authentication.js');
			return AUTHENTICATION;
		},
		configuration: async () => {
			const { CONFIGURATION } = await import('../../../configuration/development.config.js');
			return CONFIGURATION;
		},
		styles: async () => {
			const { STYLES } = await import('../../../configuration/development.styles.js');
			return STYLES;
		},
	};
}

const { isNullOrEmpty } = common.commonUtils;

export class AppStore extends stores.base.AppStoreBase<IConfiguration, IComponentStyles> {
	@observable summaryBlocksProps: ISummaryBlocksProps | undefined;
	@observable cardProps: ICardProps | undefined;
	constructor(
		element: HTMLElement,
		url: string,
		storeContext: TStoreContext,
		manifest: IManifest,
		mockData?: MockData,
	) {
		super(element, url, storeContext, manifest, Feature, devConfig, mockData);
		makeObservable(this);
	}

	@computed
	get hasData(): boolean {
		const valid =
			this.summaryBlocksProps &&
			Array.isArray(this.summaryBlocksProps.blocks) &&
			this.summaryBlocksProps.blocks.reduce((hasValues: boolean, block: ISummaryBlockProps) => {
				const hasValue = factHasValue(block);
				if ((hasValue || block.blankDataOptions?.showBlank) && !block.hide) {
					hasValues = true;
				}
				return hasValues;
			}, false);
		return valid === true;
	}

	@action
	contextsDidUpdateAfter = async () => {
		this.updateCardProps();
		this.updateSummaryBlocksProps();
	};

	@computed
	get components(): IAppComponents {
		return {
			/* [Component: appStoreComponent] */
		};
	}

	@action
	updateCardProps() {
		const cardProps = this.appComponentConfiguration && this.appComponentConfiguration.cardProps;
		this.cardProps = this.mergeQueriesAndProps(cardProps);
	}

	@action
	updateSummaryBlocksProps(){
		const summaryBlocks = this.appComponentConfiguration && this.appComponentConfiguration.summaryBlocks;
		this.summaryBlocksProps = this.mergeQueriesAndProps(summaryBlocks);
	}

	// eslint-disable-next-line @typescript-eslint/no-empty-function
	async customInitializeAfter() {}
}
